import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Wheel } from "spin-wheel"

export default function SelectAskFor() {
  const [show, setShow] = useState(null)
  const [games, setGames] = useState([])
  const [selectedGameId, setSelectedGameId] = useState("")
  const [selectedGame, setSelectedGame] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const wheelContainerRef = useRef(null)
  const wheelInstance = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const response = await fetch("/api/admin/suggestions")
      const data = await response.json()
      setShow(data)
      setGames(data.games || [])
      setLoading(false)
    } catch (error) {
      console.error("Error fetching show:", error)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedGameId) {
      const game = games.find(g => g.id.toString() === selectedGameId)
      setSelectedGame(game)
      setError(null)

      // Check if formatted_askfors is empty
      if (game && (!game.formatted_askfors || game.formatted_askfors.length === 0)) {
        setError("No items available for this game")
      } else if (game && game.formatted_askfors && game.formatted_askfors.length > 0) {
        // Delay wheel creation to ensure DOM is ready
        setTimeout(() => {
          createWheel(game.formatted_askfors)
        }, 100)
      }
    } else {
      setSelectedGame(null)
      setError(null)
      if (wheelInstance.current) {
        wheelInstance.current.remove()
        wheelInstance.current = null
      }
    }
  }, [selectedGameId, games])

  const createWheel = (formattedAskfors) => {
    // Clear existing wheel
    if (wheelInstance.current) {
      wheelInstance.current.remove()
      wheelInstance.current = null
    }

    if (!wheelContainerRef.current || formattedAskfors.length === 0) {
      return
    }

    // Clear the container
    wheelContainerRef.current.innerHTML = ""

    // Create color palette for items
    const colors = [
      "#06b6d4", // cyan-500
      "#8b5cf6", // violet-500
      "#ec4899", // pink-500
      "#f59e0b", // amber-500
      "#10b981", // emerald-500
      "#3b82f6", // blue-500
      "#6366f1", // indigo-500
      "#a855f7", // purple-500
    ]

    // Prepare wheel items from formatted_askfors array (strings)
    const items = formattedAskfors.map((item, index) => ({
      label: item,
      backgroundColor: colors[index % colors.length]
    }))

    try {
      // Create the wheel with configuration
      const container = wheelContainerRef.current
      wheelInstance.current = new Wheel(container, {
        items: items,
        pointerAngle: 90 // Pointer at right (3 o'clock position)
      })
    } catch (error) {
      console.error("Error creating wheel:", error)
    }
  }

  const handleBack = () => {
    setSelectedGameId("")
    setSelectedGame(null)
    setError(null)
  }

  const handleClose = () => {
    navigate("/")
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 p-4">
      <div className="max-w-full mx-auto px-8">
        <h1 className="text-3xl font-bold text-white text-center mb-4">
          {show?.title || "Select Ask For"}
        </h1>

        {/* Game Selection */}
        {!selectedGame ? (
          <div className="bg-white rounded-lg shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Select a Game
            </h2>

            {games.length === 0 ? (
              <p className="text-gray-600">No games with askfors available for this show.</p>
            ) : (
              <div className="space-y-4">
                {games.map((game) => (
                  <button
                    key={game.id}
                    onClick={() => setSelectedGameId(game.id.toString())}
                    className="w-full p-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg shadow-md transition-all text-left"
                    style={{
                      background: 'linear-gradient(to right, #06b6d4, #3b82f6)',
                      color: 'white',
                      border: '2px solid #0891b2'
                    }}
                  >
                    <div className="font-semibold text-lg" style={{ color: 'white' }}>
                      {game.game_type}
                    </div>
                    <div className="text-sm opacity-90 mt-1" style={{ color: 'white' }}>
                      {game.formatted_askfors?.length || 0} items
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-xl p-4">
            {/* Back Button */}
            <button
              onClick={handleBack}
              className="mb-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-sm transition-colors"
            >
              ← Back to Games
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-3">
              {selectedGame.game_type}
            </h2>

            {error ? (
              <div className="text-center py-8">
                <p className="text-red-600 text-xl mb-4">{error}</p>
                <button
                  onClick={handleBack}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-lg transition-colors"
                >
                  Go Back
                </button>
              </div>
            ) : selectedGame.formatted_askfors && selectedGame.formatted_askfors.length > 0 ? (
              <div>
                {/* Wheel and Button - Side by Side */}
                <div className="flex items-center justify-center gap-8" style={{ height: "95vh" }}>
                  {/* Wheel Container - Centered */}
                  <div style={{ position: "relative", height: "100%", flex: "0 1 auto", display: "flex", alignItems: "center" }}>
                    {/* Pointer/Indicator - Right side (3 o'clock) */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "0",
                        transform: "translateY(-50%)",
                        width: "0",
                        height: "0",
                        borderTop: "25px solid transparent",
                        borderBottom: "25px solid transparent",
                        borderRight: "40px solid #ef4444",
                        zIndex: 10,
                        filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))"
                      }}
                    />

                    <div
                      ref={wheelContainerRef}
                      className="flex justify-center items-center"
                      style={{ width: "95vh", height: "95vh", maxWidth: "95vh", maxHeight: "95vh" }}
                    />
                  </div>

                  {/* Spin Button - To the right, vertically centered */}
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        if (wheelInstance.current && selectedGame.formatted_askfors) {
                          // Pick a random item to land on
                          const randomIndex = Math.floor(Math.random() * selectedGame.formatted_askfors.length)

                          // Spin to the randomly selected item
                          wheelInstance.current.spinToItem(
                            randomIndex,
                            2500, // 2.5 second spin duration
                            true, // spin to center
                            3,    // 3 full revolutions before landing
                            1     // clockwise direction
                          )
                        }
                      }}
                      className="px-12 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold rounded-lg shadow-2xl transition-all"
                      style={{
                        background: 'linear-gradient(to right, #06b6d4, #3b82f6)',
                        color: 'white',
                        fontSize: '32px',
                        fontWeight: 'bold',
                        border: '4px solid #0891b2',
                        maxWidth: '300px',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center'
                      }}
                    >
                      SPIN THE WHEEL
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="font-semibold text-base mb-2">Raw Audience Suggestions:</h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {selectedGame.ask_for?.suggestions?.map((suggestion, index) => (
                      <div
                        key={suggestion.id}
                        className="p-2 bg-gray-100 rounded text-sm"
                      >
                        <span className="font-semibold text-gray-600">#{index + 1}:</span> {suggestion.content}
                      </div>
                    )) || <p className="text-gray-500 text-sm">No suggestions submitted yet.</p>}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
